import { TypingDots } from "@circle-react-shared/AiSummary/TypingDots";
import { Icon as LegacyIcon } from "@circle-react-shared/Icon";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { toLocaleString } from "@circle-react/helpers/number";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { usePostConversationSummary } from "@circle-react/hooks/posts/usePostConversationSummary";
import type { Post } from "@circle-react/types/Post";
import { Button } from "@circleco/compass/components/Button";
import { Icon } from "@circleco/compass/components/Icon";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import { Typography } from "@circleco/compass/components/Typography";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { FadingTextEffect } from "@/react/components/shared/FadingTextEffect";

import { DisabledNoticeV4 } from "./DisabledNoticeV4";

interface ConversationSummaryV4Props {
  className?: string;
  post: Post;
}

const LOCALE_ROOT = "post.conversation_summary";

const LIST_STYLES =
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_p+ol]:py-4 [&_p+ul]:py-4 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5";

const LOADING_GRADIENT_TEXT =
  "bg-gradient-to-r from-[#3C53E7] to-[#8994DC] bg-clip-text text-transparent";

const SHADOW_DEFAULT = "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]";
const SHADOW_ELEVATED =
  "shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]";
const SHADOW_ELEVATED_HOVER =
  "group-hover:shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]";

const MODAL_USED_IN_VALUES = new Set([
  "full-post-modal",
  "mobile-full-carousel-modal",
  "minimal-post-modal",
  "profile-modal",
]);

export const ConversationSummaryV4 = ({
  post,
  className,
}: ConversationSummaryV4Props) => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const { usedIn } = useCurrentPostContext();
  const isInModal = MODAL_USED_IN_VALUES.has(usedIn ?? "");
  const wrapperPadding = isInModal ? "py-5" : "py-5 md:px-6";
  const {
    conversationSummary,
    refetchConversationSummary,
    clearConversationSummary,
    updateConversationSummary,
  } = usePostConversationSummary({ post });
  const [isOpen, setIsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasPlayedRevealAnimation, setHasPlayedRevealAnimation] =
    useState(false);

  const handleExpandClick = () => {
    setIsOpen(true);
    if (!conversationSummary) {
      void refetchConversationSummary();
    }
  };

  const handleCollapseClick = () => {
    setIsOpen(false);
    setHasPlayedRevealAnimation(false);
    clearConversationSummary();
  };

  const handleSetDisabled = async (disabled: boolean) => {
    setIsDisabled(disabled);
    if (disabled) setIsOpen(false);
    try {
      await updateConversationSummary({ disabled });
    } catch {
      setIsDisabled(!disabled);
    }
  };

  if (!post.conversation_summary_overview) {
    return null;
  }

  if (isDisabled && isAdmin) {
    return (
      <div className={classNames(wrapperPadding, className)}>
        <DisabledNoticeV4 onUndo={() => void handleSetDisabled(false)} />
      </div>
    );
  }

  const hasContent = Boolean(conversationSummary?.content);
  const isRevealing = isOpen && hasContent && !hasPlayedRevealAnimation;
  const isRevealed = isOpen && hasContent && hasPlayedRevealAnimation;
  const shouldShowLoadingHeader = isOpen && !hasPlayedRevealAnimation;

  return (
    <div className={classNames(wrapperPadding, className)}>
      <div className="group relative">
        <div
          className={classNames(
            "border-primary bg-primary rounded-xl border px-5 py-[18px]",
            "transition-all delay-200 duration-300 ease-in-out",
            isOpen ? SHADOW_ELEVATED : SHADOW_DEFAULT,
            !isOpen &&
              `group-hover:pb-14 group-hover:delay-0 ${SHADOW_ELEVATED_HOVER}`,
            isOpen && hasContent && "pr-12",
          )}
        >
          {shouldShowLoadingHeader && (
            <div className="flex min-h-8 items-center gap-1.5">
              <TypingDots />
              <span
                className={classNames(
                  "text-sm font-medium leading-5",
                  LOADING_GRADIENT_TEXT,
                )}
              >
                {t([LOCALE_ROOT, "loading"])}
              </span>
            </div>
          )}
          {hasContent && isOpen && (
            <div className={classNames(shouldShowLoadingHeader && "mt-2")}>
              <Typography
                component="div"
                variant="body-sm"
                color="primary"
                className={LIST_STYLES}
              >
                <FadingTextEffect
                  text={conversationSummary?.content ?? ""}
                  active={isRevealing}
                  onComplete={() => setHasPlayedRevealAnimation(true)}
                  containerClassName="whitespace-pre-wrap"
                />
              </Typography>
              {post.comments_count > 0 && isRevealed && (
                <>
                  <div className="bg-tertiary mt-4 h-px w-full" />
                  <div className="mt-4 flex items-center gap-1.5">
                    <Icon name="sparkle" size="sm" color="tertiary" />
                    <Typography variant="caption" color="tertiary">
                      {t([LOCALE_ROOT, "comments_summarized"], {
                        count: post.comments_count,
                        formatted_count: toLocaleString(post.comments_count),
                      })}
                    </Typography>
                  </div>
                </>
              )}
            </div>
          )}
          {!isOpen && (
            <div className="flex flex-col gap-2">
              <div className="flex min-h-8 items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="relative inline-flex size-5 items-center justify-center">
                    <LegacyIcon
                      type="20-stardust"
                      size={20}
                      className="text-dark absolute inset-0 transition-opacity duration-300 group-hover:opacity-0"
                    />
                    <LegacyIcon
                      type="20-stardust-gradient"
                      size={20}
                      className="absolute inset-0 scale-[0.875] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                  </span>
                  <Typography variant="label-sm" color="primary">
                    {t([LOCALE_ROOT, "title"])}
                  </Typography>
                </div>
                {isAdmin && (
                  <Menu
                    align="end"
                    options={[
                      {
                        label: t([LOCALE_ROOT, "actions", "disable"]),
                        danger: true,
                        onClick: () => void handleSetDisabled(true),
                      },
                    ]}
                    trigger={
                      <IconButton
                        icon="dot-menu"
                        size="sm"
                        variant="ghost"
                        aria-label={t([LOCALE_ROOT, "actions", "title"])}
                      />
                    }
                  />
                )}
              </div>
              <Typography variant="body-sm" color="primary">
                {post.conversation_summary_overview}
              </Typography>
            </div>
          )}
        </div>
        {!isOpen && (
          <div className="pointer-events-none absolute bottom-[18px] left-5 opacity-0 transition-opacity delay-0 duration-300 ease-in-out group-hover:pointer-events-auto group-hover:opacity-100 group-hover:delay-200">
            <Button variant="outline" size="sm" onClick={handleExpandClick}>
              {t([LOCALE_ROOT, "expand"])}
            </Button>
          </div>
        )}
        {isOpen && hasContent && (
          <div
            className={classNames(
              "absolute right-2 top-2 transition-opacity duration-300",
              isRevealed ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            <IconButton
              icon="cross"
              size="sm"
              variant="ghost"
              aria-label={t([LOCALE_ROOT, "actions", "collapse"])}
              onClick={handleCollapseClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};
